import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../context/AuthContext";
import api from "../lib/api";
import { Lock, Mail, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("login");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        nav("/");
      } else {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that account exists, a reset link has been sent.");
        setMode("login");
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#fcfffd]">
      {/* Background Liquid Motion Elements */}
      <div className="absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2">
        <div className="absolute -left-32 -top-32 h-96 w-96 animate-blob rounded-full bg-[#c0fdfb] opacity-60 mix-blend-multiply blur-[100px] filter"></div>
        <div className="animation-delay-2000 absolute -right-32 -top-10 h-80 w-80 animate-blob rounded-full bg-[#daffef] opacity-70 mix-blend-multiply blur-[100px] filter"></div>
        <div className="animation-delay-4000 absolute -bottom-40 left-10 h-96 w-96 animate-blob rounded-full bg-[#64b6ac] opacity-30 mix-blend-multiply blur-[128px] filter"></div>
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="mb-8 text-center">
          <img src="/logo.jpg" alt="CensusPulse" className="mx-auto h-20 w-20 rounded-2xl object-cover shadow-lg border border-[#c0fdfb]/50" />
        </div>

        <div className="rounded-3xl border border-[#c0fdfb]/40 bg-white/60 p-8 shadow-xl backdrop-blur-xl">
          <h2 className="text-center font-display text-2xl font-bold tracking-tight text-[#5d737e]">
            {mode === "login" ? "CensusPulse Sign In" : "Reset password"}
          </h2>
          <p className="mt-2 text-center text-sm text-[#5d737e]/80">
            {mode === "login"
              ? "Secure access for verified providers."
              : "Enter your account email to receive a secure link."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-[#5d737e]">
                Email
              </label>
              <div className="flex items-center gap-3 rounded-xl border border-[#c0fdfb] bg-white px-4 focus-within:border-[#64b6ac] transition-colors shadow-sm">
                <Mail size={16} className="text-[#5d737e]/60" />
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent py-3 text-sm text-[#5d737e] placeholder-[#5d737e]/40 outline-none"
                  placeholder="you@organization.org"
                />
              </div>
            </div>

            {mode === "login" && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-xs font-bold uppercase tracking-widest text-[#5d737e]">
                    Password
                  </label>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-[#c0fdfb] bg-white px-4 focus-within:border-[#64b6ac] transition-colors shadow-sm">
                  <Lock size={16} className="text-[#5d737e]/60" />
                  <input
                    data-testid="login-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-transparent py-3 text-sm text-[#5d737e] placeholder-[#5d737e]/40 outline-none"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            )}

            {err && (
              <div data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                {err}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="mt-2 w-full rounded-xl bg-[#64b6ac] py-3.5 text-sm font-bold text-white shadow-[0_4px_14px_rgba(100,182,172,0.3)] transition-all hover:bg-[#64b6ac]/90 hover:shadow-[0_6px_20px_rgba(100,182,172,0.4)] disabled:opacity-60"
            >
              {busy ? "Authenticating…" : mode === "login" ? "Secure Sign In" : "Send reset link"}
            </button>

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-forgot"
                onClick={() => {
                  setErr("");
                  setMode(mode === "login" ? "forgot" : "login");
                }}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-[#5d737e]/70 transition-colors hover:text-[#64b6ac]"
              >
                {mode === "login" ? (
                  "Forgot password?"
                ) : (
                  <>
                    <ArrowLeft size={14} /> Back to sign in
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
